import * as XLSX from 'xlsx';

/**
 * Export questions to Excel format
 * @param {Array} questions - Array of question objects
 * @param {string} filename - Name of the file to download
 */
export const exportQuestionsToExcel = (questions, filename = 'questions_export.xlsx') => {
  try {
    // Excel cells cannot hold more than 32767 characters
    const EXCEL_CELL_LIMIT = 32767;
    let truncated = false;
    const clampCell = (value) => {
      if (typeof value === 'string' && value.length > EXCEL_CELL_LIMIT) {
        truncated = true;
        return value.slice(0, EXCEL_CELL_LIMIT);
      }
      return value;
    };

    const exportData = questions.map((q) => {
      const row = {
        'Type': '',
        'Question': clampCell(q.question || ''),
        'Option A': '',
        'Option B': '',
        'Option C': '',
        'Option D': '',
        'Correct Answer': ''
      };

      // Handle different question types - use spec format
      if (q.type === 'true_or_false') {
        // True/False: No options, correct_answer is TRUE/FALSE
        row['Type'] = 'true_or_false';
        row['Correct Answer'] = q.correct_answer 
          ? (q.correct_answer.toString().toUpperCase() === 'TRUE' ? 'TRUE' : 'FALSE')
          : '';
      } else if (q.type === 'text') {
        // Text: No options, correct_answer contains key points
        row['Type'] = 'text';
        row['Correct Answer'] = clampCell(q.correct_answer || '');
      } else {
        // Multiple Choice: Has options, correct_answer is one of them
        // Use "multi_choice" as per spec (not "multiple_choice")
        row['Type'] = 'multi_choice';
        const options = q.options || [];
        if (options.length > 0) row['Option A'] = clampCell(options[0] || '');
        if (options.length > 1) row['Option B'] = clampCell(options[1] || '');
        if (options.length > 2) row['Option C'] = clampCell(options[2] || '');
        if (options.length > 3) row['Option D'] = clampCell(options[3] || '');

        // Set correct answer (can be one of the options or the option text)
        row['Correct Answer'] = clampCell(q.correct_answer || '');
      }

      return row;
    });

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    
    // Set column widths for better readability
    worksheet['!cols'] = [
      { wch: 15 }, // Type
      { wch: 50 }, // Question
      { wch: 30 }, // Option A
      { wch: 30 }, // Option B
      { wch: 30 }, // Option C
      { wch: 30 }, // Option D
      { wch: 30 }, // Correct Answer
    ];

    // Create workbook and add worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Questions');

    // Download file
    XLSX.writeFile(workbook, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);

    if (truncated) {
      console.warn('One or more question fields exceeded the Excel cell limit (32767 characters) and were truncated.');
    }

    return { success: true, truncated };
  } catch (error) {
    console.error('Error exporting questions to Excel:', error);
    throw error;
  }
};

/**
 * Parse questions from pasted text (copied from Word, Google Docs, PDF, etc.)
 * Supports multiple formats: numbered/unnumbered, A/B/C/D or a)/b)/c)/d),
 * answer lines, asterisk-marked correct answers, bold+checkmark markdown,
 * true/false, and open-ended questions.
 * @param {string} text - Raw pasted text containing questions
 * @returns {Array} Array of parsed question objects
 */
export const parseQuestionsFromText = (text) => {
  if (!text || !text.trim()) return [];

  // Helper: strip markdown formatting from text
  const stripMarkdown = (str) => str
    .replace(/\*\*/g, '')       // bold **text**
    .replace(/✅/g, '')          // checkmark emoji
    .replace(/\*([^*]+)\*/g, '$1') // italic *text*
    .trim();

  // Helper: check if a line is marked as correct (bold+checkmark, checkmark alone, or bold wrapping)
  const isMarkedCorrect = (rawLine) =>
    /✅/.test(rawLine) || (/\*\*/.test(rawLine) && /\*\*[^*]+\*\*/.test(rawLine));

  // Phase 1: Normalize line endings and clean up
  let normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  normalized = normalized.replace(/\n{3,}/g, '\n\n').trim();

  // Phase 1b: Strip markdown structural elements
  normalized = normalized
    .split('\n')
    .filter(line => {
      const trimmed = line.trim();
      // Remove markdown headers (# Title), horizontal rules (---), and empty blockquotes
      if (/^#{1,6}\s+/.test(trimmed)) return false;
      if (/^-{3,}$/.test(trimmed)) return false;
      if (trimmed === '>') return false;
      return true;
    })
    .map(line => {
      // Strip blockquote prefix but keep the text (for passage context)
      return line.replace(/^>\s*/, '');
    })
    .join('\n');

  // Phase 2: Split into question blocks
  // Match numbered questions: "1. ", "1) ", "10. ", etc.
  // Also match "Question 1", "Question 2", etc.
  let blocks = [];
  const numberedPattern = /^[ \t]*\d+[.)]\s+\S/;
  const questionWordPattern = /^[ \t]*question\s+\d+\s*$/i;

  // Determine which pattern to use for splitting
  const hasNumberedQuestions = numberedPattern.test(normalized);
  const hasQuestionWordFormat = questionWordPattern.test(normalized.split('\n').find(l => l.trim()) || '');

  // Check all lines for "Question N" pattern
  const lines = normalized.split('\n');
  const hasQuestionDelimiters = lines.some(l => questionWordPattern.test(l.trim()));

  if (hasQuestionDelimiters) {
    // Split on "Question N" boundaries
    let currentBlock = [];
    for (const line of lines) {
      if (questionWordPattern.test(line.trim()) && currentBlock.length > 0) {
        blocks.push(currentBlock.join('\n'));
        currentBlock = []; // Don't include the "Question N" line itself — it's just a delimiter
      } else if (questionWordPattern.test(line.trim()) && currentBlock.length === 0) {
        // First "Question N" line — skip it
        continue;
      } else {
        currentBlock.push(line);
      }
    }
    if (currentBlock.length > 0) {
      blocks.push(currentBlock.join('\n'));
    }
  } else if (hasNumberedQuestions) {
    // Split on "1. ", "2) " etc. boundaries
    let currentBlock = [];
    for (const line of lines) {
      if (numberedPattern.test(line) && currentBlock.length > 0) {
        blocks.push(currentBlock.join('\n'));
        currentBlock = [line];
      } else {
        currentBlock.push(line);
      }
    }
    if (currentBlock.length > 0) {
      blocks.push(currentBlock.join('\n'));
    }
  }

  // Fallback: split on double blank lines if previous methods found ≤1 block
  if (blocks.length <= 1) {
    blocks = normalized.split(/\n\s*\n/).filter(b => b.trim());
  }

  if (blocks.length === 0) return [];

  // Phase 3: Parse each block
  const optionPattern = /^[ \t]*\**[A-Da-d][.)]\s+/;
  const answerPattern = /^[ \t]*\**\s*answer\s*[:=.]?\s*/i;
  // Standalone True/False line (with optional bold/checkmark): **True ✅**, False, etc.
  const standaloneTFPattern = /^\**\s*(true|false)\s*✅?\s*\**$/i;
  // Lines that are just bold answer text: **Ate ✅**, **He, She** etc.
  const boldAnswerPattern = /^\*\*(.+?)(?:\s*✅)?\s*\*\*$/;

  const questions = blocks.map((block, index) => {
    const rawLines = block.split('\n').map(l => l.trimEnd());

    let questionLines = [];
    let options = [];
    let answerIndicator = '';
    let markedCorrectIndex = -1;
    let detectedTFAnswer = null; // for standalone True/False lines

    for (const rawLine of rawLines) {
      const trimmed = rawLine.trim();
      if (!trimmed) continue;

      // Check for "Answer: X" line
      if (answerPattern.test(stripMarkdown(trimmed))) {
        answerIndicator = stripMarkdown(trimmed).replace(/^answer\s*[:=.]?\s*/i, '').trim();
        continue;
      }

      // Check for option line: A. text, **B. text ✅**, B. text ✅, etc.
      // Strip leading ** before checking the letter pattern
      const strippedForOptionCheck = trimmed.replace(/^\*\*\s*/, '');
      if (/^[A-Da-d][.)]\s+/.test(strippedForOptionCheck)) {
        const markedAsCorrect = isMarkedCorrect(trimmed);
        // Extract option text: strip letter prefix and markdown
        let optionText = stripMarkdown(strippedForOptionCheck.replace(/^[A-Da-d][.)]\s+/, ''));
        // Also check for trailing asterisk (non-markdown format: "Option *")
        // But avoid matching if it's just leftover from markdown stripping
        const rawOptionAfterPrefix = strippedForOptionCheck.replace(/^[A-Da-d][.)]\s+/, '');
        if (/\s+\*\s*$/.test(rawOptionAfterPrefix) && !/✅/.test(rawOptionAfterPrefix) && !/\*\*/.test(rawOptionAfterPrefix)) {
          optionText = optionText.replace(/\s*\*\s*$/, '').trim();
          markedCorrectIndex = options.length;
        } else if (markedAsCorrect) {
          markedCorrectIndex = options.length;
        }
        options.push(optionText);
        continue;
      }

      // Check for standalone True/False answer line (no A./B. prefix)
      if (standaloneTFPattern.test(trimmed) && questionLines.length > 0 && options.length === 0) {
        const tfMatch = stripMarkdown(trimmed).toLowerCase();
        detectedTFAnswer = tfMatch.startsWith('true') ? 'true' : 'false';
        continue;
      }

      // Check for bold answer line (e.g. **Ate ✅**, **He, She**)
      // Only treat as answer if we already have question text and no options
      const boldMatch = trimmed.match(boldAnswerPattern);
      if (boldMatch && questionLines.length > 0 && options.length === 0 && !answerIndicator) {
        answerIndicator = boldMatch[1].trim();
        continue;
      }

      // Check for "Example answer:" or similar prefix lines — skip them
      if (/^(example\s+answer|correct\s+answer|other\s+correct|any\s+correct)\s*[:.]?\s*$/i.test(trimmed)) {
        continue;
      }

      // Parenthetical notes like "(Correct plural: children)" or "(It is an adjective)" — skip
      if (/^\(.*\)$/.test(trimmed)) {
        continue;
      }

      // Question text line (only collect before first option/answer/TF detection)
      if (options.length === 0 && !answerIndicator && detectedTFAnswer === null) {
        questionLines.push(stripMarkdown(trimmed));
      }
    }

    // Clean question text: strip leading number prefix or (a)/(b) prefix
    let questionText = questionLines.join(' ')
      .replace(/^\d+[.)]\s*/, '')        // "1. " or "1) " prefix
      .replace(/^\([a-z]\)\s*/i, '')     // "(a) " prefix
      .trim();

    if (!questionText) return null;

    // Determine type
    let type = 'multiple_choice';
    if (detectedTFAnswer !== null && options.length === 0) {
      // Standalone True/False detected (no A/B options, just True or False line)
      type = 'true_or_false';
    } else if (options.length === 0) {
      type = 'text';
    } else if (
      options.length === 2 &&
      options[0].toLowerCase() === 'true' &&
      options[1].toLowerCase() === 'false'
    ) {
      type = 'true_or_false';
    }

    // Resolve correct answer
    let correctAnswer = '';

    if (type === 'true_or_false') {
      if (detectedTFAnswer !== null) {
        // Standalone True/False line was detected
        correctAnswer = detectedTFAnswer;
      } else if (markedCorrectIndex >= 0) {
        // Bold/checkmark on A. True or B. False
        correctAnswer = markedCorrectIndex === 0 ? 'true' : 'false';
      } else if (answerIndicator) {
        const upper = answerIndicator.toUpperCase();
        if (upper === 'A' || upper === 'TRUE') {
          correctAnswer = 'true';
        } else if (upper === 'B' || upper === 'FALSE') {
          correctAnswer = 'false';
        }
      }
      options = []; // T/F type stores no options
    } else if (type === 'multiple_choice') {
      if (markedCorrectIndex >= 0 && markedCorrectIndex < options.length) {
        // Bold/checkmark or asterisk marked correct
        correctAnswer = options[markedCorrectIndex];
      } else if (answerIndicator) {
        // Check if it's a single letter (A-D)
        const letterMatch = answerIndicator.match(/^[A-Da-d]$/);
        if (letterMatch) {
          const letterIndex = letterMatch[0].toUpperCase().charCodeAt(0) - 65; // A=0, B=1, ...
          if (letterIndex >= 0 && letterIndex < options.length) {
            correctAnswer = options[letterIndex];
          }
        } else {
          // Try to match full text against options
          const match = options.find(
            opt => opt.toLowerCase() === answerIndicator.toLowerCase()
          );
          correctAnswer = match || answerIndicator;
        }
      }
    } else {
      // Text type - answer indicator becomes the scoring guide
      correctAnswer = stripMarkdown(answerIndicator);
    }

    return {
      id: Date.now() + index,
      question: questionText,
      type,
      options: type === 'multiple_choice' ? options : [],
      correct_answer: correctAnswer,
      is_active: true,
      point: undefined,
    };
  }).filter(q => q !== null);

  return questions;
};

/**
 * Import questions from Excel/CSV file
 * @param {File} file - The file to import
 * @returns {Promise<Array>} Array of parsed question objects
 */
export const importQuestionsFromFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        
        // Get the first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
          defval: '', // Default value for empty cells
          raw: false // Get formatted values as strings
        });
        
        // Parse the data into question format
        // Normalize column names (case-insensitive, flexible matching)
        const normalizeKey = (key, row) => {
          const lowerKey = key.toLowerCase().trim();
          // Try exact match first
          if (row[key] !== undefined) return key;
          // Try case-insensitive match
          const foundKey = Object.keys(row).find(k => k.toLowerCase().trim() === lowerKey);
          if (foundKey) return foundKey;
          // Try common variations
          const variations = {
            'type': ['Type', 'TYPE', 'Question Type', 'question_type'],
            'question': ['Question', 'QUESTION', 'Question Text', 'question_text'],
            'option a': ['Option A', 'OPTION A', 'option_a', 'Option_A'],
            'option b': ['Option B', 'OPTION B', 'option_b', 'Option_B'],
            'option c': ['Option C', 'OPTION C', 'option_c', 'Option_C'],
            'option d': ['Option D', 'OPTION D', 'option_d', 'Option_D'],
            'correct answer': ['Correct Answer', 'CORRECT ANSWER', 'correct_answer', 'Correct_Answer', 'Answer']
          };
          const variation = variations[lowerKey];
          if (variation) {
            const found = variation.find(v => row[v] !== undefined);
            if (found) return found;
          }
          return key; // Return original if not found
        };

        const questions = jsonData
          .map((row, index) => {
            try {
              const typeKey = normalizeKey('Type', row);
              const questionKey = normalizeKey('Question', row);
              const type = (row[typeKey] || '').trim().toLowerCase();
              const question = (row[questionKey] || '').trim();
              
              // Skip empty rows
              if (!question) {
                return null;
              }
              
              // Normalize question type - accept both "multi_choice" (spec) and "multiple_choice" (codebase)
              let normalizedType = 'multiple_choice'; // Use codebase format internally
              if (type === 'true_or_false' || type === 'true/false' || type === 'true or false') {
                normalizedType = 'true_or_false';
              } else if (type === 'text' || type === 'short_answer' || type === 'descriptive') {
                normalizedType = 'text';
              } else if (type === 'multi_choice' || type === 'multiple_choice' || type === 'multiple choice' || type === '') {
                // Default to multiple_choice if empty or any variation
                normalizedType = 'multiple_choice';
              }
              
              const questionObj = {
                id: Date.now() + index, // Generate temporary ID
                question: question,
                type: normalizedType,
                is_active: true
              };
              
              if (normalizedType === 'true_or_false') {
                // True/False: Parse correct answer
                const correctAnswerKey = normalizeKey('Correct Answer', row);
                const correctAnswer = (row[correctAnswerKey] || '').trim().toUpperCase();
                questionObj.correct_answer = correctAnswer === 'TRUE' ? 'true' : 'false';
                questionObj.options = [];
              } else if (normalizedType === 'text') {
                // Text: Correct answer contains key points
                const correctAnswerKey = normalizeKey('Correct Answer', row);
                questionObj.correct_answer = (row[correctAnswerKey] || '').trim();
                questionObj.options = [];
              } else {
                // Multiple Choice: Parse options and correct answer
                const options = [];
                const optionAKey = normalizeKey('Option A', row);
                const optionBKey = normalizeKey('Option B', row);
                const optionCKey = normalizeKey('Option C', row);
                const optionDKey = normalizeKey('Option D', row);
                const correctAnswerKey = normalizeKey('Correct Answer', row);
                
                const optionA = (row[optionAKey] || '').trim();
                const optionB = (row[optionBKey] || '').trim();
                const optionC = (row[optionCKey] || '').trim();
                const optionD = (row[optionDKey] || '').trim();
                
                if (optionA) options.push(optionA);
                if (optionB) options.push(optionB);
                if (optionC) options.push(optionC);
                if (optionD) options.push(optionD);
                
                // Ensure at least one option (default if none provided)
                if (options.length === 0) {
                  options.push('Option 1');
                }
                
                questionObj.options = options;
                
                // Set correct answer - try to match with options first
                const correctAnswer = (row[correctAnswerKey] || '').trim();
                if (correctAnswer) {
                  // Check if correct answer matches any option
                  const matchingOption = options.find(opt => opt.trim() === correctAnswer.trim());
                  questionObj.correct_answer = matchingOption || correctAnswer;
                }
              }
              
              return questionObj;
            } catch (error) {
              console.error(`Error parsing row ${index + 1}:`, error);
              return null;
            }
          })
          .filter(q => q !== null); // Remove null entries
        
        if (questions.length === 0) {
          reject(new Error('No valid questions found in the file. Please check the format.'));
          return;
        }
        
        resolve(questions);
      } catch (error) {
        console.error('Error importing questions:', error);
        reject(error);
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    // Read the file as binary string
    reader.readAsBinaryString(file);
  });
};

